import {ref} from "vue";
import axios from "axios";
import debounce from "lodash-es/debounce.js";
import {route} from "ziggy-js";

export const ROBLOX_USERNAME_SEARCH_DEBOUNCE_MS = 1200;

// Состояния поиска
// 0 - начальное состояние (пустое поле)
// 1 - показываем результаты поиска
// 2 - поиск в процессе
// 3 - пользователь не найден
export const SEARCH_STATUS_IDLE = 0;
export const SEARCH_STATUS_RESULTS = 1;
export const SEARCH_STATUS_LOADING = 2;
export const SEARCH_STATUS_NOT_FOUND = 3;

export interface RobloxUserSearchResult {
    id: number;
    name: string;
    displayName: string;
    hasVerifiedBadge: boolean;
    avatarUrl?: string;
}

/**
 * Поиск аккаунта Roblox по нику.
 *
 * Каждый запрос к roblox.find-username расходует квоту одного из наших Roblox-аккаунтов
 * (у поисковых эндпоинтов Roblox лимиты вплоть до 1 запроса в минуту), поэтому здесь
 * важно не отправлять лишнего:
 *  - повторный поиск той же строки не уходит на сервер вовсе;
 *  - предыдущий запрос отменяется, как только пользователь дописал символ, иначе
 *    быстрый набор оставляет несколько параллельных запросов в полете, и каждый
 *    из них жжет отдельный аккаунт ради ответа, который уже никому не нужен;
 *  - ответ отмененного запроса игнорируется и не может «перебить» свежий результат.
 */
export function useRobloxUsernameSearch(minLength: number = 3) {
    const status = ref(SEARCH_STATUS_IDLE);
    const searchResults = ref<RobloxUserSearchResult[]>([]);
    const isLoading = ref(false);

    let controller: AbortController | null = null;
    let lastQuery = '';

    const cancelInFlight = (): void => {
        if (controller) {
            controller.abort();
            controller = null;
        }
    };

    const reset = (): void => {
        cancelInFlight();
        lastQuery = '';
        isLoading.value = false;
        status.value = SEARCH_STATUS_IDLE;
        searchResults.value = [];
    };

    const search = (username: string): void => {
        const query = (username ?? '').trim();

        if (!query || query.length < minLength) {
            reset();
            return;
        }

        // Та же строка уже ищется или уже найдена — второй раз спрашивать нечего
        if (query === lastQuery) {
            return;
        }

        cancelInFlight();

        const current = new AbortController();
        controller = current;
        lastQuery = query;

        isLoading.value = true;
        status.value = SEARCH_STATUS_LOADING;

        axios.post(route('roblox.find-username'), {
            username: query,
        }, {
            signal: current.signal,
        }).then(response => {
            if (controller !== current) {
                return;
            }

            const users = response.data?.success ? (response.data.data.users || []) : [];

            searchResults.value = users;
            status.value = users.length > 0 ? SEARCH_STATUS_RESULTS : SEARCH_STATUS_NOT_FOUND;
        }).catch(error => {
            if (axios.isCancel(error) || controller !== current) {
                return; // запрос отменен более свежим вводом — это не ошибка
            }

            // Даем повторить тот же ник: ошибка могла быть временной
            lastQuery = '';

            if (error.response?.status === 429) {
                // Слишком частые запросы: прежние результаты все еще актуальны,
                // не показываем вместо них «аккаунт не найден» — но и не оставляем
                // висеть спиннер, поиск для этого ввода закончен
                console.warn('Roblox username search throttled');
                status.value = searchResults.value.length > 0
                    ? SEARCH_STATUS_RESULTS
                    : SEARCH_STATUS_IDLE;
                return;
            }

            console.error('Error searching users:', error);
            status.value = SEARCH_STATUS_NOT_FOUND;
            searchResults.value = [];
        }).finally(() => {
            if (controller === current) {
                controller = null;
                isLoading.value = false;
            }
        });
    };

    const debouncedSearch = debounce(search, ROBLOX_USERNAME_SEARCH_DEBOUNCE_MS);

    return {status, searchResults, isLoading, search, debouncedSearch, reset};
}
